import { FormikHelpers } from 'formik'
import * as React from 'react'
import styled from 'styled-components'

import { LoginHeading } from '@ally/ally-login'
import { Form, Forms, useFormsContext } from '@ally/forms'
import {
  Button,
  CardSelectionCard,
  CardSelectionGroup,
  ScreenReaderOnly,
} from '@ally/metronome-ui'
import { AsyncStatus, SelectAccountProps } from '@ally/transmitigator'

import { useHostServices } from '../../HostServices'
import { useTransmit } from '../../TransmitContext'
import { FeatureFlag } from '../../Utils'
import { CIAMErrorBanner } from '../Shared'

const FORM_ID = 'ally-login-forgot-username-select-account'
const AUTO = 'auto'
const BANK = 'bank'
const GUEST = 'guest'

type AccountTypeOption = typeof AUTO | typeof BANK | typeof GUEST

interface SelectAccountFormProps {
  status: AsyncStatus
}

const StyledCardSelectionGroup = styled(CardSelectionGroup)`
  ${({ theme: { media, space } }): string => `
    ${media.up('md')} {
      max-width: 300px;
    }
    margin-bottom: ${space.md}
  `}
`

export const SelectAccountForm: React.FC<SelectAccountFormProps> = ({
  status,
}) => {
  const { setFieldValue } = useFormsContext()
  const { featureFlags } = useHostServices()
  const isGuestExperienceEnabled = featureFlags.variation(
    FeatureFlag.GuestExperience,
  )

  return (
    <Form id={FORM_ID}>
      <ScreenReaderOnly id="select-account-type-label">
        Which account do you want to log in to?
      </ScreenReaderOnly>
      <StyledCardSelectionGroup
        variant="single-select"
        align="stacked"
        defaultSelection={BANK}
        id="selectAccountType"
        ariaLabelledby="select-account-type-label"
        onSelection={(cardId: any): void => {
          setFieldValue('accountType', cardId)
        }}
      >
        <CardSelectionCard
          cardId={BANK}
          title="Bank, Invest, Credit Card or Home Loans"
        />
        <CardSelectionCard cardId={AUTO} title="Auto" />
        {isGuestExperienceEnabled && (
          <CardSelectionCard
            cardId={GUEST}
            title="FPO - Manage Debt Tool/Wealth"
          />
        )}
      </StyledCardSelectionGroup>

      <Button
        text="Continue"
        loading={status === 'Loading'}
        type="submit"
        variant="primary"
        allytmln="forgotPassword-accountType-continue"
      />
    </Form>
  )
}

interface SelectAccountFormValueProps {
  accountType: AccountTypeOption
}

const initialValues: SelectAccountFormValueProps = {
  accountType: BANK, // defaults to 'bank'
}

export const SelectAccountContent: React.FC<SelectAccountProps> = ({
  onComplete,
  status,
  error,
}) => {
  const { hostServices } = useTransmit()

  const handleSubmit = (
    form: SelectAccountFormValueProps,
    { setSubmitting }: FormikHelpers<SelectAccountFormValueProps>,
  ): void => {
    onComplete({
      auto: form.accountType === AUTO,
      bank: form.accountType === BANK,
      guest: form.accountType === GUEST,
    })

    // Required to allow form resubmit after failure
    setSubmitting(false)
  }

  React.useEffect(() => {
    hostServices?.analytics.trackPageView({
      eventType: 'pageview',
      pageName: 'AOS:Login:Forgot Username:Account Type',
    })
  }, [])

  return (
    <>
      <LoginHeading title="Which account do you want to log in to?" />

      <CIAMErrorBanner error={error} />

      <Forms
        formId={FORM_ID}
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        <SelectAccountForm status={status} />
      </Forms>
    </>
  )
}

export const SelectAccount: React.FC = () => {
  const { activeScreen } = useTransmit()

  if (activeScreen.type !== 'SelectAccount') {
    return null
  }

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <SelectAccountContent {...activeScreen.payload} />
  )
}
